import Buildup from './../Buildup.vue';
import BuildupDrinks from './../BuildupDrinks.vue';
import FormUtils from './../../../utils/FormUtils.js';
import ProductFormComments from './../ProductFormComments';
import ToggleSwitch from './../../common/ToggleSwitch.vue';
import vueDraggable from 'vuedraggable';
import Swal from 'sweetalert2';

const DOWNLOAD_ENDPOINTS = Object.freeze({
  pdf: (productId) => `/products/download-pdf/${productId}`,
  image: (productId) => `/products/download-image/${productId}`
});

const normalizeExtension = (extension) => (extension || '').toString().replace(/^\./, '').toLowerCase();

const sanitizeFileBaseName = (name, fallback) => {
  const baseName = (name || fallback || '').toString();
  const safeName = baseName
    .replace(/[^a-zA-Z0-9-_ąćęłńóśźżĄĆĘŁŃÓŚŹŻ ]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return safeName || (fallback ? fallback.toString() : 'plik');
};

const getSafeFileName = (name, id, extension) => {
  const safeBaseName = sanitizeFileBaseName(name, `produkt-${id}`);
  const ext = normalizeExtension(extension);
  return ext ? `${safeBaseName}.${ext}` : safeBaseName;
};

const getPdfStateKey = (format) => `download${String(format).toUpperCase()}PdfInProgress`;
const getImageStateKeyPart = (imageFormat) => {
  const normalized = normalizeExtension(imageFormat);
  const map = {
    png: 'Png',
    jpg: 'Jpg',
    jpeg: 'Jpg'
  };

  return map[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '');
};

const getImageStateKey = (format, imageFormat) => `download${String(format).toUpperCase()}${getImageStateKeyPart(imageFormat)}InProgress`;

export default {
  name: 'product-form',
  components: {
    'buildup': Buildup,
    'buildup-drinks': BuildupDrinks,
    'draggable': vueDraggable,
    'product-form-comments': ProductFormComments,
    'toggle-switch': ToggleSwitch
  },
  computed: {
    allDataLoaded() {
      return this.loaded && this.packagesLoaded && this.typesLoaded && this.ingredientsLoaded && this.unitsLoaded && this.statusesLoaded;
    },
    isEdit() {
      return !!this.$route.params.id;
    },
    packageOptions() {
      return this.packages.map(pkg => ({
        id: pkg.id,
        label: pkg.name
      }));
    },
    typeOptions() {
      // Show only parent categories (without subcategories)
      return this.types
        .filter(type => !type.parent_id)
        .map(type => ({
          id: type.id,
          label: type.name
        }));
    },
    subcategoryOptions() {
      if (!this.data.type_id) {
        return [];
      }
      const options = this.subcategories
        .filter(subcat => subcat.parent_id === this.data.type_id);

      // If API loaded a subcategory object (rare), ensure it's available in options
      if (this.loadedSubcategoryObject && !options.some(opt => opt.id === this.loadedSubcategoryObject.id)) {
        options.push(this.loadedSubcategoryObject);
      }

      return options;
    },
    statusOptions() {
      return this.statuses.map(status => ({
        id: status.id,
        label: status.name,
        color: status.color
      }));
    },
    useDrinkEditor() {
      let currentType = this.types.filter(type => type.id === this.data.type_id);

      if (currentType[0]) {
        return !!currentType[0].use_drink_editor;
      }

      return false;
    },
    ingredientsAddons() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_drink && ingredient.is_addon);
    },
    ingredientsFood() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_food);
    },
    ingredientsCovers() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_drink && ingredient.is_cover);
    },
    ingredientsToppings() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_drink && ingredient.is_topping);
    },
    ingredientsFluids() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_drink && !ingredient.is_addon && !ingredient.is_cover && !ingredient.is_topping);
    },
    ingredientsDrinks() {
      return (this.ingredients || []).filter(ingredient => ingredient.available_in_drink);
    },
    totalFluidAmount() {
      return this.data.fluids.reduce((sum, fluid) => sum + parseInt(fluid.quantity, 10), 0);
    },
    fluidsQuantities() {
      return this.data.fluids.map(fluid => parseInt(fluid.quantity, 10));
    },
    drinkIngredients() {
      if (!this.data.fluids || !Array.isArray(this.data.fluids)) {
        return [];
      }
      return this.data.fluids.filter((ingredient) => ingredient && ingredient.ingredient_id).map(ingredient => ({
        id: parseInt(ingredient.id, 10),
        name: this.getIngredientNameById(parseInt(ingredient.ingredient_id, 10))
      }));
    },
    fontSizeOptions() {
      const options = [];
      for (let i = 5; i <= 50; i++) {
        options.push({ text: `${i}`, value: i });
      }
      return options;
    },
    isBigBlock() {
      let blocks = {};

      for (let i = 0; i < this.data.blocks.length; i++) {
        let block = this.data.blocks[i];
        // Skip null/undefined blocks or blocks without ingredient_id
        if (!block || !block.id) {
          continue;
        }
        let value = this.checkIfIsBigIngredient(block.ingredient_id || null);
        blocks[block.id] = value;
      }

      return blocks;
    }
  },
  watch: {
    // data.subcategory_id is directly bound to the select via v-model
    useDrinkEditor(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.setEditView('fluids');
      } else if (newValue !== oldValue && !newValue) {
        this.setEditView('blocks');
        // Clear drink-specific data when switching away from drink editor
        if (!this.isEdit) {
          this.data.fluids = [];
          this.data.addons = [];
          this.data.steps = [];
        }
      }
    },
    'data.type_id'(newValue, oldValue) {
      // Jeśli oldValue jest nullem, to znaczy że właśnie wczytaliśmy dane z bazy.
      // Nie chcemy wtedy resetować subcategory_id!
      if (oldValue === null || oldValue === undefined) {
        return;
      }

      const normalizedNewValue = newValue ? parseInt(newValue, 10) || null : null;
      const normalizedOldValue = oldValue ? parseInt(oldValue, 10) || null : null;

      if (normalizedNewValue !== normalizedOldValue) {
        this.data.subcategory_id = null;
        this.selectedSubcategory = null;
      }
    },
    fluidsQuantities: {
      handler(newValue, oldValue) {
        let newQuantity = newValue.reduce((sum, fluid) => sum + fluid, 0);

        if (newQuantity > 100) {
          for (let i = 0; i < newValue.length; i++) {
            if (newValue[i] !== oldValue[i]) {
              this.data.fluids[i].quantity = oldValue[i];
            }
          }
        }

        for (let i = 0; i < newValue.length; i++) {
          if (newValue[i] < 0) {
            this.data.fluids[i].quantity = oldValue[i];
          }
        }
      },
      deep: true
    },
    allDataLoaded(newValue) {
      console.log('allDataLoaded watcher', newValue, 'data.subcategory_id', this.data.subcategory_id, 'subcategoryOptions', this.subcategoryOptions);
      if (newValue && this.isEdit && this.data.subcategory_id) {
        this.selectedSubcategory = this.subcategoryOptions.find(opt => opt.id === this.data.subcategory_id) || null;
        console.log('set selectedSubcategory to', this.selectedSubcategory);
      }
      if (newValue && this.isEdit) {
        this.autoSaveInterval = setTimeout(() => {
          this.runAutoSave();
        }, this.autoSaveTime);
      }
    }
  },
  data() {
    return {
      data: {
        isInitialLoad: true,
        id: 0,
        archived_at: null,
        name: '',
        project_name: '',
        type_id: null,
        subcategory_id: null,
        package_id: null,
        status_id: null,
        show_package: false,
        image: null,
        image_url: '',
        blocks: [],
        fluids: [],
        addons: [],
        layers: [],
        steps: [],
        use_cover_ingredient: false,
        cover_ingredient: {
          ingredient_id: null,
          comment: ''
        },
        use_topping_ingredient: false,
        topping_ingredient: [
          { ingredient_id: null, amount: '', comment: '' },
          { ingredient_id: null, amount: '', comment: '' }
        ],
        size: ''
      },
      state: {
        selectedBlock: null,
        selectedIndex: false,
        downloadA4PdfInProgress: false,
        downloadB5PdfInProgress: false,
        downloadA4PngInProgress: false,
        downloadA4JpgInProgress: false,
        downloadB5PngInProgress: false,
        downloadB5JpgInProgress: false
      },
      editView: 'blocks',
      packages: [],
      types: [],
      subcategories: [],
      selectedSubcategory: null,
      loadedSubcategoryObject: null,
      statuses: [],
      ingredients: [],
      units: [],
      unitsLoaded: false,
      ingredientsLoaded: false,
      loaded: false,
      packagesLoaded: false,
      typesLoaded: false,
      statusesLoaded: false,
      loadError: false,
      autoSaveTime: 100 * 1000,
      showAutoSaveNotice: false,
      autoSaveInterval: null
    };
  },
  mounted() {
    this.loadAdditionalData();
    
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }

    this.$bus.on('buildup-layer-move', this.updateLayerPosition);
    this.$bus.on('buildup-addon-move', this.updateAddonPosition);
    this.$bus.on('buildup-layer-select', this.toggleBlockSelection);
    this.$bus.on('buildup-update-drink-size', this.updateDrinkSize);
    this.$bus.on('buildup-set-cover-ingredient', this.setCoverIngredient);
    this.$bus.on('buildup-set-topping-ingredient', this.setToppingIngredient);
    this.$bus.on('buildup-reset-cover-ingredient', this.resetCoverIngredient);
    this.$bus.on('buildup-reset-topping-ingredient', this.resetToppingIngredient);
  },
  beforeDestroy() {
    this.selectedSubcategory = null;
    this.$bus.off('buildup-layer-move', this.updateLayerPosition);
    this.$bus.off('buildup-addon-move', this.updateAddonPosition);
    this.$bus.off('buildup-layer-select', this.toggleBlockSelection);
    this.$bus.off('buildup-update-drink-size', this.updateDrinkSize);
    this.$bus.off('buildup-set-cover-ingredient', this.setCoverIngredient);
    this.$bus.off('buildup-set-topping-ingredient', this.setToppingIngredient);
    this.$bus.off('buildup-reset-cover-ingredient', this.resetCoverIngredient);
    this.$bus.off('buildup-reset-topping-ingredient', this.resetToppingIngredient);
  },
  methods: {
    showDuplicateModal() {
      this.$swal({
        title: 'Potwierdzenie duplikacji',
        html: 'Czy na pewno chcesz zduplikować PRODUKT - <b>' + (this.data.name || '') + '</b>?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Duplikuj',
        cancelButtonText: 'Anuluj',
        buttonsStyling: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.duplicateProduct();
        }
      });
    },
    duplicateProduct() {
      this.$http
        .post('/products/duplicate/' + this.data.id)
        .then((response) => {
          if (response.data.success) {
            // Sprawdź różne możliwe struktury odpowiedzi backendu
            const newProductId = response.data.new_id || 
                                 response.data.id || 
                                 response.data.product_id || 
                                 (response.data.item && response.data.item.id) ||
                                 (response.data.product && response.data.product.id);
            
            if (newProductId) {
              this.$swal({
                title: 'Sukces',
                text: response.data.message || 'Produkt został zduplikowany',
                icon: 'success',
              }).then(() => {
                // Przekierowanie do nowo utworzonego produktu
                this.$router.push('/products/' + newProductId);
              });
            } else {
              // Jeśli nie ma ID, po prostu pokaż komunikat
              this.$swal({
                title: 'Sukces',
                text: response.data.message || 'Produkt został zduplikowany',
                icon: 'success',
              });
            }
          } else {
            this.$swal({
              title: 'Błąd',
              text: response.data.message || 'Nie udało się zduplikować produktu',
              icon: 'error',
            });
          }
        })
        .catch((error) => {
          this.$swal({
            title: 'Błąd',
            text: 'Wystąpił problem podczas duplikowania produktu',
            icon: 'error',
          });
          console.error(error);
        });
    },
    loadItemData() {
      FormUtils.loadItemData(this, {
        endpointBase: '/products/item/',
        instanceFields: ['id', 'name', 'project_name', 'type_id', 'subcategory_id', 'package_id', 'status_id', 'show_package', 'blocks', 'size', 'image_url', 'layers', 'fluids', 'addons', 'steps', 'cover_ingredient', 'topping_ingredient', 'use_cover_ingredient', 'use_topping_ingredient', 'archived_at'],
        booleanFields: ['show_package', 'use_cover_ingredient', 'use_topping_ingredient'],
        loadedKey: 'loaded',
        successAction: (responseData) => {
          // Normalize IDs to ensure proper type matching
          const subIdFromServer = responseData.subcategory_id ? parseInt(responseData.subcategory_id, 10) : null;
          this.data.type_id = parseInt(responseData.type_id, 10) || null;
          this.data.subcategory_id = subIdFromServer;
          this.data.package_id = parseInt(responseData.package_id, 10) || null;

          if (responseData.subcategory) {
            this.loadedSubcategoryObject = responseData.subcategory;
          }

          if (this.allDataLoaded) {
            this.selectedSubcategory = this.subcategoryOptions.find(opt => opt.id === this.data.subcategory_id) || null;
          }

          // Set array fields from response
          this.data.blocks = responseData.blocks || [];
          this.data.layers = responseData.layers || [];
          this.data.fluids = responseData.fluids || [];
          this.data.addons = responseData.addons || [];
          this.data.steps = responseData.steps || [];

          // Save the subcategory object for later use (optional; API may return null)
          if (responseData.subcategory) {
            this.loadedSubcategoryObject = responseData.subcategory;
            const existingSubcat = this.subcategories.find(s => s && s.id === responseData.subcategory.id);
            if (!existingSubcat) {
              this.subcategories.push(responseData.subcategory);
            }
          }

          if (!this.data.blocks) {
            this.data.blocks = [];
          } else {
            this.data.blocks = this.data.blocks.filter(b => b);
          }

          if (!this.data.layers) {
            this.data.layers = [];
          } else {
            this.data.layers = this.data.layers.filter(l => l);
          }

          if (!this.data.fluids) {
            this.data.fluids = [];
          } else {
            this.data.fluids = this.data.fluids.filter(f => f);
          }

          if (!this.data.addons) {
            this.data.addons = [];
          } else {
            this.data.addons = this.data.addons.filter(a => a);
          }

          if (!this.data.steps) {
            this.data.steps = [];
          } else {
            this.data.steps = this.data.steps.filter(s => s);
          }

          this.data.addons = this.data.addons.map(addon => {
            addon.ingredient_id = addon.ingredient_id ? parseInt(addon.ingredient_id, 10) : null;
            return addon;
          });

          this.data.fluids = this.data.fluids.map(fluid => {
            fluid.ingredient_id = fluid.ingredient_id ? parseInt(fluid.ingredient_id, 10) : null;
            return fluid;
          });

          // Ensure drink ingredient structures are always safe for template v-model bindings.
          // API can return null or unexpected shapes; Vue template expects objects/arrays.
          const rawCoverIngredient = this.data.cover_ingredient;
          if (!rawCoverIngredient || typeof rawCoverIngredient !== 'object' || Array.isArray(rawCoverIngredient)) {
            this.data.cover_ingredient = {
              ingredient_id: rawCoverIngredient ? parseInt(rawCoverIngredient, 10) || null : null,
              comment: ''
            };
          } else {
            if (typeof this.data.cover_ingredient.ingredient_id !== 'undefined' && this.data.cover_ingredient.ingredient_id !== null && this.data.cover_ingredient.ingredient_id !== '') {
              this.data.cover_ingredient.ingredient_id = parseInt(this.data.cover_ingredient.ingredient_id, 10) || null;
            } else {
              this.data.cover_ingredient.ingredient_id = null;
            }

            if (typeof this.data.cover_ingredient.comment === 'undefined' || this.data.cover_ingredient.comment === null) {
              this.data.cover_ingredient.comment = '';
            }
          }

          const rawToppingIngredient = this.data.topping_ingredient;
          if (typeof rawToppingIngredient === 'number' || typeof rawToppingIngredient === 'string' || rawToppingIngredient === null) {
            this.data.topping_ingredient = [
              { ingredient_id: rawToppingIngredient ? parseInt(rawToppingIngredient, 10) || null : null, amount: '', comment: '' },
              { ingredient_id: null, amount: '', comment: '' }
            ];
          } else if (!Array.isArray(rawToppingIngredient)) {
            this.data.topping_ingredient = [
              { ingredient_id: null, amount: '', comment: '' },
              { ingredient_id: null, amount: '', comment: '' }
            ];
          } else {
            const first = rawToppingIngredient[0] && typeof rawToppingIngredient[0] === 'object' ? rawToppingIngredient[0] : {};
            const second = rawToppingIngredient[1] && typeof rawToppingIngredient[1] === 'object' ? rawToppingIngredient[1] : {};

            this.data.topping_ingredient = [
              {
                ingredient_id: first.ingredient_id ? parseInt(first.ingredient_id, 10) || null : null,
                amount: typeof first.amount === 'undefined' || first.amount === null ? '' : first.amount,
                comment: typeof first.comment === 'undefined' || first.comment === null ? '' : first.comment
              },
              {
                ingredient_id: second.ingredient_id ? parseInt(second.ingredient_id, 10) || null : null,
                amount: typeof second.amount === 'undefined' || second.amount === null ? '' : second.amount,
                comment: typeof second.comment === 'undefined' || second.comment === null ? '' : second.comment
              }
            ];
          }

          this.data.use_cover_ingredient = !!this.data.use_cover_ingredient;
          this.data.use_topping_ingredient = !!this.data.use_topping_ingredient;

          this.data.blocks = this.data.blocks.map(block => {
            if (block.type === 'ingredient') {
              block.is_optional = !!block.is_optional;
              block.is_seasonal = !!block.is_seasonal;
              block.is_underline = !!block.is_underline;
              block.font_size = block.font_size || 24;
            } else if (block.type === 'columns' && block.columnsContent) {
              block.columnsContent = block.columnsContent.map(column => {
                if (column && column.type === 'ingredient') {
                  column.font_size = column.font_size || 24;
                }
                return column;
              });
            }

            return block;
          });

          this.$nextTick(() => {
            this.isInitialLoad = false;
          });
        }
      });
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/packages/items',
        params: {
          pagination: 0
        },
        outputKey: 'packages',
        loadedKey: 'packagesLoaded',
        successAction: (packages) => {
          this.packages = packages.filter(p => p);
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/ingredients/items',
        params: {
          pagination: 0
        },
        outputKey: 'ingredients',
        loadedKey: 'ingredientsLoaded',
        successAction: (ingredients) => {
          this.ingredients = ingredients.filter(i => i);
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/system/units/items',
        params: {
          pagination: 0
        },
        outputKey: 'units',
        loadedKey: 'unitsLoaded',
        successAction: (units) => {
          this.units = units.filter(u => u);
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/products/types/items',
        params: {
          pagination: 0
        },
        outputKey: 'types',
        loadedKey: 'typesLoaded',
        successAction: () => {
          // Load all types (including subcategories) for subcategory dropdown
          this.loadSubcategories();
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/system/statuses/items',
        params: {
          pagination: 0
        },
        outputKey: 'statuses',
        loadedKey: 'statusesLoaded'
      });
    },
    loadSubcategories() {
      // Extract all subcategories from loaded types (which have children)
      const newSubcategories = [];
      this.types.forEach(type => {
        if (type.children && type.children.length > 0) {
          newSubcategories.push(...type.children.filter(c => c));
        }
      });
      
      // Always include the loaded subcategory object if it exists
      if (this.loadedSubcategoryObject) {
        const exists = newSubcategories.some(s => s.id === this.loadedSubcategoryObject.id);
        if (!exists) {
          newSubcategories.push(this.loadedSubcategoryObject);
        }
      }
      
      this.subcategories = newSubcategories;
    },
    onTypeChange() {
      // Clear subcategory when type changes
      this.data.subcategory_id = null;
      this.selectedSubcategory = null;
  
      // Jeśli przełączasz na kategorię, która NIE jest drinkiem, 
      // upewnij się, że struktura topping_ingredient jest zachowana jako puste stringi, a nie null
      if (!this.useDrinkEditor) {
        this.data.topping_ingredient = [
          { ingredient_id: null, amount: '', comment: '' },
          { ingredient_id: null, amount: '', comment: '' }
        ];
      }
    },
    onSubcategoryChange(value) {
      // value to obiekt np. { id: 10, name: 'Podkategoria' } lub null
      if (value && typeof value === 'object') {
        this.data.subcategory_id = value.id;
        this.selectedSubcategory = value;
      } else {
        this.data.subcategory_id = null;
        this.selectedSubcategory = null;
      }
    },
    validateForm() {
      FormUtils.validate(this.$refs['product-form'], this.submitForm);
    },
    submitForm() {
      let endpointUrl = this.isEdit ? '/products/update/' + this.id : '/products/store';

      if (this.data.image !== null) {
        FormUtils.submit(this, {
          endpoint: endpointUrl,
          formData: {
            name: this.data.name,
            project_name: this.data.project_name,
            type_id: this.data.type_id,
            subcategory_id: this.selectedSubcategory ? this.selectedSubcategory.id : null,
            package_id: this.data.package_id,
            status_id: this.data.status_id,
            show_package: this.data.show_package,
            image: this.data.image,
            blocks: !this.useDrinkEditor ? this.data.blocks : [],
            layers: this.data.layers,
            fluids: this.useDrinkEditor ? this.prepareFluidsData() : [],
            addons: this.useDrinkEditor ? this.data.addons : [],
            steps: this.useDrinkEditor ? this.data.steps : [],
            cover_ingredient: this.useDrinkEditor ? this.data.cover_ingredient : null,
            topping_ingredient: this.useDrinkEditor ? this.data.topping_ingredient : null,
            use_cover_ingredient: this.data.use_cover_ingredient,
            use_topping_ingredient: this.data.use_topping_ingredient,
            size: this.useDrinkEditor ? this.data.size : null
          },
          successTitle: 'Zapisano produkt',
          successText: 'Wybrany produkt został zapisany',
          errorTitle: 'Wystąpił błąd',
          errorText: 'Zapis nie powiódł się. Spróbuj ponownie.',
          successAction: (response) => {
            if (!this.isEdit && response.success) {
              this.$router.push('/products/' + parseInt(response.item.id, 10));
            } else if (this.isEdit && response.success && response.item) {
              // Update local data with server response to sync subcategory_id and other fields
              this.data.subcategory_id = response.item.subcategory_id;
              this.data.type_id = response.item.type_id;
            }

            clearTimeout(this.autoSaveInterval);
            this.autoSaveInterval = setTimeout(() => {
              this.runAutoSave();
            }, this.autoSaveTime);
          }
        }, ['image']);
      } else {
        FormUtils.submit(this, {
          endpoint: endpointUrl,
          formData: {
            name: this.data.name,
            project_name: this.data.project_name,
            type_id: this.data.type_id,
            subcategory_id: this.selectedSubcategory ? this.selectedSubcategory.id : null,
            package_id: this.data.package_id,
            status_id: this.data.status_id,
            show_package: this.data.show_package,
            blocks: !this.useDrinkEditor ? this.data.blocks : [],
            layers: this.data.layers,
            fluids: this.useDrinkEditor ? this.prepareFluidsData() : [],
            addons: this.useDrinkEditor ? this.data.addons : [],
            steps: this.useDrinkEditor ? this.data.steps : [],
            cover_ingredient: this.useDrinkEditor ? this.data.cover_ingredient : null,
            topping_ingredient: this.useDrinkEditor ? this.data.topping_ingredient : null,
            use_cover_ingredient: this.data.use_cover_ingredient,
            use_topping_ingredient: this.data.use_topping_ingredient,
            size: this.useDrinkEditor ? this.data.size : null
          },
          successTitle: 'Zapisano produkt',
          successText: 'Wybrany produkt został zapisany',
          errorTitle: 'Wystąpił błąd',
          errorText: 'Zapis nie powiódł się. Spróbuj ponownie.',
          successAction: (response) => {
            if (!this.isEdit && response.success) {
              this.$router.push('/products/' + parseInt(response.item.id, 10));
            } else if (this.isEdit && response.success && response.item) {
              // Update local data with server response to sync subcategory_id and other fields
              this.data.subcategory_id = response.item.subcategory_id;
              this.data.type_id = response.item.type_id;
            }

            clearTimeout(this.autoSaveInterval);
            this.autoSaveInterval = setTimeout(() => {
              this.runAutoSave();
            }, this.autoSaveTime);
          }
        });
      }
    },
    setImage(event) {
      this.data.image = event.target.files[0];
    },
    removeImage() {
      this.data.image_url = '';
      this.data.image = null;
    },
    addIngredient() {
      this.data.blocks.unshift({
        id: +new Date(),
        type: 'ingredient',
        ingredient_id: null,
        quantity: 1,
        unit_id: null,
        label: '',
        label_position: 'top',
        font_size: 24,
        is_optional: false,
        is_seasonal: false,
        is_underline: false
      });
    },
    addSpacer() {
      this.data.blocks.unshift({
        id: +new Date(),
        type: 'spacer',
        space: 'medium', // small - 50px | medium - 100px | big - 200px
      });
    },
    addIngredientAndClose() {
      this.addIngredient();
      this.closeDropdown('addBlockDropdown');
    },
    addSpacerAndClose() {
      this.addSpacer();
      this.closeDropdown('addBlockDropdown');
    },
    closeDropdown(refName) {
      if (this.$refs[refName] && this.$refs[refName].$el) {
        const button = this.$refs[refName].$el.querySelector('.btn');
        if (button) {
          button.click();
        }
      }
    },
    resetIngredientParams(index) {
      let ingredientUnits = this.ingredientUnits(parseInt(this.data.blocks[index].ingredient_id, 10));
      this.data.blocks[index].quantity = 1;
      this.data.blocks[index].unit_id = null;
      this.data.blocks[index].is_optional = false;
      this.data.blocks[index].is_seasonal = false;
      this.data.blocks[index].is_underline = false;
      this.data.blocks[index].label = '';
      this.data.blocks[index].label_position = 'top';

      if (ingredientUnits.length === 1) {
        this.data.blocks[index].unit_id = ingredientUnits[0].id;
      }
    },
    removeBlock(index) {
      this.data.blocks.splice(index, 1);
    },
    ingredientUnits(ingredientID) {
      let foundedIngredient = (this.ingredients || []).filter(ingredient => ingredient.id === ingredientID)[0];

      if (foundedIngredient && foundedIngredient.units) {
        return foundedIngredient.units.map(unit => unit.unit)
      }

      return [];
    },
    ingredientIsCountable(ingredientIndex) {
      let unitID = this.data.blocks[ingredientIndex].unit_id;

      if (unitID && this.units.length) {
        return !!this.units.filter(unit => unit.id === unitID)[0].is_countable;
      }

      return false;
    },
    updateLayerPosition(movedItem, x, y) {
      let layerToUpdate = this.data.layers.filter(layer => layer.id === movedItem.itemID);

      if (!layerToUpdate[0]) {
        return;
      }

      layerToUpdate[0].position.x = x;
      layerToUpdate[0].position.y = y;
    },
    updateAddonPosition(movedItem, x, y) {
      let addonToUpdate = this.data.addons.filter(addon => addon.id === movedItem.itemID);

      if (!addonToUpdate[0]) {
        return;
      }

      addonToUpdate[0].position.x = x;
      addonToUpdate[0].position.y = y;
    },
    setEditView(view) {
      this.editView = view;
      this.state.selectedBlock = null;
    },
    toggleBlockSelection(itemID) {
      if (this.state.selectedBlock === itemID) {
        this.state.selectedBlock = null;
      } else {
        this.state.selectedBlock = itemID;
      }
    },
    addTextLayer() {
      this.data.layers.unshift({
        id: +new Date(),
        text: '',
        textAlign: 'center',
        textColor: 'dark',
        position: {
          x: 400,
          y: 25
        }
      });
    },
    removeLayer(index) {
      this.data.layers.splice(index, 1);
    },
    addFluidLayer() {
      let fluidAmount = 100 - this.totalFluidAmount;

      if (fluidAmount <= 0) {
        this.$swal({
          title: 'Brak miejsca na nowy składnik',
          html: 'Zmniejsz rozmiar już dodanych składników aby zrobić miejsce na nowy składnik.',
          icon: 'error',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });

        return;
      }

      this.data.fluids.unshift({
        id: +new Date(),
        ingredient_id: null,
        quantity: fluidAmount,
        comment: '',
        amount: ''
      });
    },
    removeFluid(index) {
      this.data.fluids.splice(index, 1);
    },
    addAddonLayer() {
      this.data.addons.unshift({
        id: +new Date(),
        ingredient_id: null,
        comment: '',
        amount: '',
        position: {
          x: 125,
          y: 250
        }
      });
    },
    removeAddon(index) {
      this.data.addons.splice(index, 1);
    },
    addStep() {
      this.data.steps.push({
        id: +new Date(),
        ingredient1_id: null,
        ingredient2_id: null,
        comment: ''
      });
    },
    removeStep(index) {
      this.data.steps.splice(index, 1);
    },
    downloadPDF(format) {
      const productId = parseInt(this.data.id, 10);
      const fileName = getSafeFileName(this.data.name, productId, 'pdf');
      const stateKey = getPdfStateKey(format);

      return this._triggerDownload({
        url: DOWNLOAD_ENDPOINTS.pdf(productId),
        payload: {
          type: 'pdf',
          paper_format: format
        },
        fileName,
        stateKey
      });
    },
    downloadImage(format, imageFormat) {
      const productId = parseInt(this.data.id, 10);
      const fileName = getSafeFileName(this.data.name, productId, imageFormat);
      const stateKey = getImageStateKey(format, imageFormat);

      return this._triggerDownload({
        url: DOWNLOAD_ENDPOINTS.image(productId),
        payload: {
          paper_format: format,
          image_format: imageFormat
        },
        fileName,
        stateKey
      });
    },
    async _triggerDownload({ url, payload, fileName, stateKey }) {
      this.state[stateKey] = true;

      try {
        const response = await this.$http.post(url, payload, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));

        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Release memory after download starts (important for repeated downloads)
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
      } catch (error) {
        console.log(error);
        this.$swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
      } finally {
        this.state[stateKey] = false;
      }
    },
    runAutoSave() {
      const currentSubId = this.selectedSubcategory ? this.selectedSubcategory.id : null;

      FormUtils.submitInBackground(this, {
        endpoint: '/products/update/' + this.id,
        params: {
          name: this.data.name,
          type_id: this.data.type_id,
          subcategory_id: currentSubId,
          package_id: this.data.package_id,
          status_id: this.data.status_id,
          show_package: this.data.show_package,
          blocks: !this.useDrinkEditor ? this.data.blocks : [],
          layers: this.data.layers,
          fluids: this.useDrinkEditor ? this.prepareFluidsData() : [],
          addons: this.useDrinkEditor ? this.data.addons : [],
          steps: this.useDrinkEditor ? this.data.steps : [],
          cover_ingredient: this.useDrinkEditor ? this.data.cover_ingredient : null,
          topping_ingredient: this.useDrinkEditor ? this.data.topping_ingredient : null,
          use_cover_ingredient: this.data.use_cover_ingredient,
          use_topping_ingredient: this.data.use_topping_ingredient,
          size: this.useDrinkEditor ? this.data.size : null
        },
        errorTitle: 'Wystąpił błąd',
        errorText: 'Automatyczny zapis nie powiódł się.',
        successAction: (response) => {
          if (this.isEdit && response.success) {
            this.autoSaveInterval = setTimeout(() => {
              this.runAutoSave();
            }, this.autoSaveTime);
          }
        },
        failAction: () => {
          this.autoSaveInterval = setTimeout(() => {
            this.runAutoSave();
          }, this.autoSaveTime);
        }
      });
    },
    setCoverIngredient() {
      this.data.use_cover_ingredient = true;
    },
    setToppingIngredient() {
      this.data.use_topping_ingredient = true;
    },
    resetCoverIngredient() {
      this.data.use_cover_ingredient = false;
    },
    resetToppingIngredient() {
      this.data.use_topping_ingredient = false;
    },
    prepareFluidsData() {
      return this.data.fluids.map(fluid => {
        if (
          this.$refs['buildup-drinks'] &&
          typeof this.$refs['buildup-drinks'].getSvgCode === 'function'
        ) {
          fluid.svg_code = this.$refs['buildup-drinks'].getSvgCode(fluid.id);
        } else {
          fluid.svg_code = '';
        }
        return fluid;
      });
    },
    updateDrinkSize(newValue) {
      this.data.size = newValue;
    },
    increaseFontSize(index) {
      if (this.data.layers[index].fontSize) {
        this.data.layers[index].fontSize = this.data.layers[index].fontSize + 1;

        if (this.data.layers[index].fontSize > 100) {
          this.data.layers[index].fontSize = 100;
        }
      } else {
        this.data.layers[index].fontSize = 25;
      }
    },
    decreaseFontSize(index) {
      if (this.data.layers[index].fontSize) {
        this.data.layers[index].fontSize = this.data.layers[index].fontSize - 1;

        if (this.data.layers[index].fontSize < 8) {
          this.data.layers[index].fontSize = 8;
        }
      } else {
        this.data.layers[index].fontSize = 23;
      }
    },
    getIngredientNameById(id) {
      let foundedIngredient = (this.ingredients || []).filter(ingredient => ingredient.id === id);

      if (foundedIngredient.length) {
        return foundedIngredient[0].name;
      }

      return 'Usunięty składnik';
    },
    setTextColor(index, color) {
      if (!this.data.layers[index].textColor) {
        this.data.layers[index].textColor = 'dark';
      }

      this.data.layers[index].textColor = color;
      this.$forceUpdate();
    },
    checkIfIsBigIngredient(ingredientID) {
      let foundedIngredient = (this.ingredients || []).filter(ingredient => ingredient.id === ingredientID);

      if (foundedIngredient && foundedIngredient[0]) {
        return !!foundedIngredient[0].is_big_ingredient;
      }

      return false;
    },
    promptArchiveProduct () {
      Swal.fire({
        title: 'Czy na pewno chcesz przenieść ten produkt do archiwum?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tak',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.isConfirmed) {
          this.archiveProduct();
        }
      });
    },
    archiveProduct () {
      this.$http.post('/products/archive/' + this.data.id).then(response => {
        if (response.data.success) {
          Swal.fire(
            'Zarchiwizowano!',
            'Produkt został przeniesiony do archiwum.',
            'success'
          ).then(() => {
            this.$router.push('/archive');
          });
        } else {
          Swal.fire('Błąd', 'Nie udało się zarchiwizować produktu.', 'error');
        }
      });
    },
    promptRestoreProduct () {
      Swal.fire({
        title: 'Czy na pewno chcesz przywrócić produkt?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.isConfirmed) {
          this.restoreProduct();
        }
      });
    },
    restoreProduct () {
      this.$http.post('/products/restore/' + this.data.id).then(response => {
        if (response.data.success) {
          Swal.fire(
            'Przywrócono!',
            'Produkt został przywrócony.',
            'success'
          ).then(() => {
            this.$router.push('/products');
          });
        } else {
          Swal.fire('Błąd', 'Nie udało się przywrócić produktu.', 'error');
        }
      });
    }
  },
  beforeDestroy() {
    this.$bus.off('buildup-layer-move', this.updateLayerPosition);
    this.$bus.off('buildup-addon-move', this.updateAddonPosition);
    this.$bus.off('buildup-layer-select', this.toggleBlockSelection);
    this.$bus.off('buildup-update-drink-size', this.updateDrinkSize);
    this.$bus.off('buildup-set-cover-ingredient', this.setCoverIngredient);
    this.$bus.off('buildup-set-topping-ingredient', this.setToppingIngredient);
    this.$bus.off('buildup-reset-cover-ingredient', this.resetCoverIngredient);
    this.$bus.off('buildup-reset-topping-ingredient', this.resetToppingIngredient);
    clearTimeout(this.autoSaveInterval);
  }
};
